import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Select, Tag as AntdTag } from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useExportCallbackRequestsMutation, useGetCallbackLogsQuery } from '@api/dashboards';
import { useGetOrganisationQuery } from '@api/organisations';
import {
  buildCustomerCallbackExportRequest,
  buildCustomerCallbackLogFilter,
  CUSTOMER_CALLBACK_DEFAULT_RANGE_DAYS,
} from '@api/callback';
import { selectOrganisationContextId } from '@selectors/appSelector';
import { usePagination } from '@shared/common/hooks/usePagination';
import SharedCallbackRequests from '@shared/components/CallbackRequests/CallbackRequests';
import { formatDate } from '@shared/helpers/dateHelper';
import { CallbackLog, CallbackStatus } from '@shared/models/callback';
import Button from '@shared/components/Button/Button';
import DateRangeFilter from '@shared/components/DateRangeFilter';
import styles from './CallbackRequests.module.scss';

dayjs.extend(utc);

const CALLBACK_STATUS_COLOURS: Record<CallbackStatus, string> = {
  [CallbackStatus.Incomplete]: 'default',
  [CallbackStatus.Sent]: 'green',
  [CallbackStatus.Failed]: 'red',
  [CallbackStatus.Queued]: 'default',
};

const CallbackRequests = () => {
  const organisationId = useSelector(selectOrganisationContextId);
  const pagination = usePagination(8, 1);
  const { currentPage, pageSize, changeCurrentPage } = pagination;

  const [filterDate, setFilterDate] = useState({
    start: dayjs().utc().subtract(CUSTOMER_CALLBACK_DEFAULT_RANGE_DAYS, 'day').startOf('day'),
    end: dayjs().utc().endOf('day'),
  });
  const [selectedKnowledgebaseIds, setSelectedKnowledgebaseIds] = useState<string[]>([]);
  const { data: organisation } = useGetOrganisationQuery(organisationId!, {
    skip: !organisationId,
  });

  useEffect(() => {
    void changeCurrentPage(1);
  }, [
    changeCurrentPage,
    filterDate.end,
    filterDate.start,
    organisationId,
    selectedKnowledgebaseIds,
  ]);

  useEffect(() => {
    setSelectedKnowledgebaseIds([]);
  }, [organisationId]);

  const queryArgs = useMemo(
    () =>
      organisationId
        ? buildCustomerCallbackLogFilter({
            organisationId,
            startDate: dayjs(filterDate.start).utc().startOf('day').toISOString(),
            endDate: dayjs(filterDate.end).utc().endOf('day').toISOString(),
            knowledgebaseIds: selectedKnowledgebaseIds,
            pageNumber: currentPage,
            pageSize,
          })
        : undefined,
    [
      currentPage,
      filterDate.end,
      filterDate.start,
      organisationId,
      pageSize,
      selectedKnowledgebaseIds,
    ],
  );

  const { data: callbackLogs, isFetching, isError } = useGetCallbackLogsQuery(queryArgs!, {
    skip: !queryArgs,
    refetchOnMountOrArgChange: true,
  });
  const [exportCallbackRequests, { isLoading: isExporting }] = useExportCallbackRequestsMutation();
  const knowledgebaseOptions = useMemo(
    () =>
      (organisation?.insights ?? [])
        .map((insight) => ({
          label: insight.display_name ?? insight.name,
          value: insight.id,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [organisation?.insights],
  );

  const columns = useMemo<TableColumnsType<CallbackLog>>(
    () => [
      {
        title: 'Requested',
        dataIndex: 'created_on',
        key: 'created_on',
        render: (value: string) => formatDate(value, { dateStyle: 'medium', timeStyle: 'short' }),
      },
      {
        title: 'Knowledgebase',
        key: 'knowledgebase_name',
        render: (_, record) => record.knowledgebase_name ?? record.insight_name,
      },
      {
        title: 'Requested by',
        dataIndex: 'full_name',
        key: 'full_name',
      },
      {
        title: 'Mobile',
        dataIndex: 'mobile',
        key: 'mobile',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'Message',
        dataIndex: 'message',
        key: 'message',
        ellipsis: true,
        render: (value: string) => <span title={value}>{value}</span>,
      },
      {
        title: 'Chat history',
        dataIndex: 'include_chat_history',
        key: 'include_chat_history',
        render: (value: boolean) => (value ? 'Included' : 'Not included'),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: CallbackStatus) => (
          <AntdTag color={CALLBACK_STATUS_COLOURS[status]}>{status}</AntdTag>
        ),
      },
    ],
    [],
  );

  const onDateFilterChange = useCallback((dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates?.[0] || !dates[1]) {
      return;
    }

    setFilterDate({
      start: dates[0].utc().startOf('day'),
      end: dates[1].utc().endOf('day'),
    });
  }, []);

  const onKnowledgebaseChange = useCallback((values: string[]) => {
    setSelectedKnowledgebaseIds(values);
  }, []);

  const onExportData = useCallback(async () => {
    if (!organisationId) {
      return;
    }

    await exportCallbackRequests(
      buildCustomerCallbackExportRequest({
        organisationId,
        startDate: dayjs(filterDate.start).utc().startOf('day').toISOString(),
        endDate: dayjs(filterDate.end).utc().endOf('day').toISOString(),
        knowledgebaseIds: selectedKnowledgebaseIds,
      }),
    ).unwrap();
  }, [
    exportCallbackRequests,
    filterDate.end,
    filterDate.start,
    organisationId,
    selectedKnowledgebaseIds,
  ]);

  if (!organisationId) {
    return null;
  }

  return (
    <SharedCallbackRequests<CallbackLog>
      title="Callback requests"
      sectionTitle="Organisation-scoped submitted callback requests"
      totalLabel={`${callbackLogs?.total_items ?? 0} total`}
      errorMessage={isError ? "Couldn't load callback requests." : undefined}
      loading={isFetching}
      rowKey="id"
      columns={columns}
      dataSource={callbackLogs?.results ?? []}
      pagination={pagination}
      totalItems={callbackLogs?.total_items ?? 0}
      toolbar={
        <div className={styles.toolbar}>
          <div className={styles.twoColumnGrid}>
            <div className={styles.field}>
              <div className={styles.fieldLabel}>Knowledge base</div>
              <Select
                allowClear
                mode="multiple"
                optionFilterProp="label"
                placeholder="All knowledge bases"
                className={styles.select}
                value={selectedKnowledgebaseIds}
                options={knowledgebaseOptions}
                onChange={onKnowledgebaseChange}
              />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldLabel}>Date</div>
              <div className={styles.select}>
                <DateRangeFilter
                  value={[filterDate.start, filterDate.end]}
                  onChange={onDateFilterChange}
                  allowEmpty={[false, false]}
                  allowClear={false}
                  disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                />
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <Button type="button" onClick={onExportData} isDisabled={isExporting}>
              {!isExporting ? 'Download XLSX' : 'Downloading...'}
            </Button>
          </div>
        </div>
      }
      pageSizeOptions={['8', '16', '24', '32', '64', '100']}
      simplePagination={false}
    />
  );
};

export default CallbackRequests;
